import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, signal } from '@angular/core';
import { Steps } from '../../../shared/components/steps/steps';
import { RadioCard } from '../../../shared/components/radio-card/radio-card';
import { Router } from '@angular/router';
import { HttpResponse } from '@angular/common/http';
import { EscolhaInstitutoApiResponse, InstitutosElegiveis } from '../../../core/models/escolha-instituto';
import { HttpClientService } from '../../../shared/services/http-client-service';
import { LoaderService } from '../../../shared/services/loader-service';
import { TIPO_INSTITUTO } from '../../../core/constants/tipo-instituto';
import { ToastService } from '../../../shared/services/toast-service';
import { LocalStorageService } from '../../../shared/services/local-storage-service';
import { FLUXO_DEFINITIVO } from '../../../core/constants/storage-keys';

/**
 * Autopatrocínio (2) e BPD (3) seguem direto para a própria rota sem salvar a escolha (o BPD vai
 * para a assinatura do termo com idInstituto=3); os demais institutos salvam a escolha
 * (saveInstituto) e seguem para os dados cadastrais.
 */
const ROTA_POR_INSTITUTO: Record<string, { rota: string; queryParams?: Record<string, number> }> = {
  [TIPO_INSTITUTO.AUTOPATROCINIO]: { rota: '/instituto-online/autopatrocinio' },
  [TIPO_INSTITUTO.BPD]: {
    rota: '/instituto-online/assinatura-termo-mvp',
    queryParams: { idInstituto: TIPO_INSTITUTO.BPD },
  },
};

/** A portabilidade não segue pelo fluxo online: o participante precisa falar com a central. */
const PORTABILIDADE = String(TIPO_INSTITUTO.PORTABILIDADE);

@Component({
  imports: [Steps, RadioCard],
  selector: 'app-escolha-instituto',
  styleUrl: './escolha-instituto.scss',
  templateUrl: './escolha-instituto.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class EscolhaInstituto {
  private readonly router = inject(Router);
  private readonly httpClientService = inject(HttpClientService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly localStorageService = inject(LocalStorageService);
  readonly stepAtual = signal(2);
  readonly totalSteps = signal(7);
  readonly institutoSelecionado = signal<string | null>(null);
  /** Institutos elegíveis retornados pela API; enquanto não responde (ou sem o campo), todos ficam habilitados. */
  readonly elegivel = signal<Partial<InstitutosElegiveis>>({});
  /** Card desabilitado só quando a API manda o instituto como não elegível (false). */
  readonly desabilitado = computed<Record<string, boolean>>(() => {
    const elegivel = this.elegivel();
    return {
      [TIPO_INSTITUTO.RESGATE]: elegivel.resgate === false,
      [TIPO_INSTITUTO.AUTOPATROCINIO]: elegivel.autopatrocinio === false,
      [TIPO_INSTITUTO.BPD]: elegivel.bpd === false,
      [TIPO_INSTITUTO.PORTABILIDADE]: elegivel.portabilidade === false,
    };
  });
  readonly portabilidadeSelecionada = computed(() => this.institutoSelecionado() === PORTABILIDADE);
  readonly podeContinuar = computed(() => !!this.institutoSelecionado() && !this.portabilidadeSelecionada());

  ngOnInit(): void {
    void this.getInstituto();
  }

  onReturn() {
    this.router.navigate(['/instituto-online/extrato-desligamento']);
  }

  async onSubmit(): Promise<void> {
    const instituto = this.institutoSelecionado();
    if (!instituto || !this.podeContinuar()) return;

    const rotaPropria = ROTA_POR_INSTITUTO[instituto];
    if (rotaPropria) {
      const { rota, queryParams } = rotaPropria;
      this.router.navigate([rota], queryParams ? { queryParams } : undefined);
      return;
    }

    this.loaderService.showLoader('Salvando...');

    try {
      await this.httpClientService.saveInstituto(Number(instituto));
      this.router.navigate([this.rotaDoResgate()]);
    } catch {
      this.toastService.show('Não foi possível salvar a escolha do instituto. Tente novamente.', 'error');
    } finally {
      this.loaderService.hideLoader();
    }
  }

  /**
   * Com fluxoDefinitivo = 'true' no localStorage (gravado pelo token-verify), o resgate segue para
   * os dados cadastrais; com 'false' ou sem a chave, para o resgate-mvp.
   */
  private rotaDoResgate(): string {
    return this.localStorageService.get(FLUXO_DEFINITIVO) === 'true'
      ? '/instituto-online/dados-cadastrais'
      : '/instituto-online/resgate-mvp';
  }

  private async getInstituto(): Promise<void> {

    this.loaderService.showLoader('Carregando...');

    try {
      const data: HttpResponse<EscolhaInstitutoApiResponse> =
        await this.httpClientService.getInstituto();
      const id = data.body?.tipoSolicitacao;
      this.elegivel.set(data.body?.elegivel ?? {});

      // Não marca um instituto já escolhido que deixou de ser elegível.
      if (id != null && !this.desabilitado()[id]) {
        this.institutoSelecionado.set(String(id));
      }
    } catch {
      this.toastService.show('O sistema está indisponível. Tente novamente.', 'error');
    } finally {
      this.loaderService.hideLoader();
    }
  }
}
